// frontend/src/components/Navbar.js
import { useState, useEffect } from 'react';
import { SUPER_ADMIN_EMAIL } from '../config/appConfig';
import { Link, useLocation } from 'react-router-dom';
import SearchBar from './SearchBar';
import SubscribeButton from './SubscribeButton';
import AuthButton from './AuthButton';
import authService from '../services/authService';
import './Navbar.css';

function Navbar() {
  const location = useLocation();

  // State-based authentication check
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    currentUser: null,
    isSuperAdmin: false
  });

  // Check authentication on mount and route changes
  useEffect(() => {
    const checkAuth = () => {
      const isAuth = authService.isAuthenticated();
      const user = authService.getCurrentUser();
      const isAdmin = user?.email === SUPER_ADMIN_EMAIL;

      // 🔍 DEBUG - Remove after fixing
      console.group('🔍 Navbar Auth Check');
      console.log('isAuthenticated:', isAuth);
      console.log('currentUser:', user);
      console.log('user email:', user?.email);
      console.log('isSuperAdmin:', isAdmin);
      console.groupEnd();

      setAuthState({
        isAuthenticated: isAuth,
        currentUser: user,
        isSuperAdmin: isAdmin
      });
    };

    checkAuth();

    // Listen for storage changes (login in another tab)
    window.addEventListener('storage', checkAuth);

    // CRITICAL: Listen for custom auth-changed event (same tab)
    window.addEventListener('auth-changed', checkAuth);

    // Poll for auth changes for 5 seconds after mount/route change
    // This handles edge cases where events don't fire
    const intervals = [];
    for (let i = 0; i < 10; i++) {
      intervals.push(setTimeout(checkAuth, i * 500)); // Check every 500ms
    }

    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('auth-changed', checkAuth);
      intervals.forEach(clearTimeout);
    };
  }, [location.pathname]); // Re-check on route change

  const isActive = (path) => {
    return location.pathname === path ? 'navbar-link active' : 'navbar-link';
  };

  const { isAuthenticated, currentUser, isSuperAdmin } = authState;

  return (
    <nav className="navbar-cnn">
      <div className="navbar-container">
        {/* Desktop Layout */}
        <div className="navbar-desktop">
          <Link to="/" className="navbar-logo-wrapper">
            <div className="navbar-logo">Focus View</div>
          </Link>

          <div className="navbar-center">
            <div className="navbar-menu">
              <Link to="/" className={isActive('/')}>
                ALL NEWS
              </Link>
              <Link to="/crypto" className={isActive('/crypto')}>
                CRYPTO
              </Link>
              <Link to="/ai" className={isActive('/ai')}>
                AI
              </Link>
              <Link to="/politics" className={isActive('/politics')}>
                POLITICS
              </Link>
            </div>
          </div>

          <div className="navbar-right">
            <SearchBar />

            {/* DASHBOARD BUTTON – only for super admin */}
            {isAuthenticated && isSuperAdmin && (
              <Link
                to="/admin/dashboard"
                className="navbar-link"
                style={{
                  marginLeft: '12px',
                  fontWeight: '600',
                  color: '#6a0dad',
                  textDecoration: 'underline',
                }}
              >
                Dashboard
              </Link>
            )}

            {/* AUTH BUTTON OR SUBSCRIBE */}
            {isAuthenticated ? (
              <AuthButton />
            ) : (
              <SubscribeButton variant="header" />
            )}
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="navbar-mobile">
          <div className="navbar-mobile-top">
            <Link to="/" className="navbar-logo-wrapper">
              <div className="navbar-logo">Focus View</div>

            </Link>
            {isAuthenticated && isSuperAdmin ? (
              <>
                <Link
                  to="/admin/dashboard"
                  className="navbar-link admin-dashboard-btn"
                  style={{
                    marginRight: '8px',
                    color: '#6a0dad',
                    fontWeight: '600'
                  }}
                >
                  Dashboard
                </Link>
                <AuthButton />
              </>
            ) : isAuthenticated ? (
              <AuthButton />
            ) : (
              <SubscribeButton variant="header" />
            )}
          </div>

          <div className="navbar-mobile-search">
            <SearchBar />
          </div>

          <div className="navbar-menu">
            <Link to="/" className={isActive('/')}>
              ALL NEWS
            </Link>
            <Link to="/crypto" className={isActive('/crypto')}>
              CRYPTO
            </Link>
            <Link to="/ai" className={isActive('/ai')}>
              AI
            </Link>
            <Link to="/politics" className={isActive('/politics')}>
              POLITICS
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;